import { useState } from 'react';
import { sessionStore } from './sessionStore';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiRequestError, GatewayUnavailableError } from '../api';
import { login as loginRequest } from './authService';
import { useSession } from './useSession';
import { Button } from '../components/Button';

/**
 * Вход в систему.
 *
 * «Запомнить это устройство» (0058) НЕ кладёт рабочий токен в localStorage —
 * решение 45 это запрещает. Запоминается отдельный одноразовый токен, который
 * не открывает данных, а только даёт право получить новую сессию, сгорает при
 * первом использовании и виден в «Моих устройствах». Галочка выключена по
 * умолчанию: на общем компьютере её включать не надо, и об этом сказано прямо.
 */
export function LoginPage() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice] = useState<string | null>(() =>
    sessionStore.takeClearReason() === 'expired' ? 'Сессия истекла — войдите снова.' : null,
  );

  if (session.token) {
    return <Navigate to="/" replace />;
  }

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await loginRequest(login, password, remember);
      navigate(from, { replace: true });
    } catch (e) {
      if (e instanceof ApiRequestError) {
        setError(e.message);
      } else if (e instanceof GatewayUnavailableError) {
        setError('Шлюз недоступен. Проверьте соединение и повторите.');
      } else {
        setError('Не удалось выполнить вход.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={onSubmit}>
        <div className="login-card__brand">WearGroup<small>Производство · Вход в систему</small></div>
        <label className="login-card__field">
          <span className="login-card__label">Логин</span>
          <input
            className="login-card__input"
            name="login"
            autoComplete="username"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            required
          />
        </label>
        <label className="login-card__field">
          <span className="login-card__label">Пароль</span>
          <input
            className="login-card__input"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <label className="login-card__remember">
          <input
            type="checkbox"
            name="remember_device"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <span>
            Запомнить это устройство
            <span className="t-secondary login-card__remember-note">
              30 дней без ввода пароля. Не включайте на чужом компьютере.
            </span>
          </span>
        </label>
        {notice !== null && error === null && (
          <div className="t-secondary" role="status">
            {notice}
          </div>
        )}
        {error !== null && (
          <div className="login-card__error" role="alert">
            {error}
          </div>
        )}
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Вход…' : 'Войти'}
        </Button>
      </form>
    </div>
  );
}
